/* ==========================================================================
   COURSE SUBJECT ENGINE
   --------------------------------------------------------------------------
   Every product in the store becomes a Midterm and a Final. Until now the
   subject of those exams was inferred by a regex that only recognised maths
   and nursing keywords — so MIS561 (Management Information Systems) fell
   through to "general" and was served algebra. That was the bug.

   This module does two things:
     1. Classifies a course from its code prefix and title into a subject.
     2. Supplies genuinely subject-relevant questions for that subject.

   Classification is by course-code prefix first (most reliable), then title
   keywords, then tags. Anything still unrecognised gets study-skills and
   research-methods items rather than arithmetic, because those at least
   apply to every course.

   Loads after exam-generator-topics.js, before exam-engine.js.
   ========================================================================== */
(function () {
  'use strict';

  function yt(q, label) {
    return { t: label || ('YouTube — ' + q), u: 'https://www.youtube.com/results?search_query=' + encodeURIComponent(q), k: 'video' };
  }
  var REF = {
    owl: { t: 'Purdue OWL — Research and Citation', u: 'https://owl.purdue.edu/owl/research_and_citation/resources.html', k: 'reference' },
    osBiz: { t: 'OpenStax — Introduction to Business', u: 'https://openstax.org/details/books/introduction-business', k: 'scholarly' },
    osMgmt: { t: 'OpenStax — Principles of Management', u: 'https://openstax.org/details/books/principles-management', k: 'scholarly' },
    osMkt: { t: 'OpenStax — Principles of Marketing', u: 'https://openstax.org/details/books/principles-marketing', k: 'scholarly' },
    osAcc: { t: 'OpenStax — Financial Accounting', u: 'https://openstax.org/details/books/principles-financial-accounting', k: 'scholarly' },
    osFin: { t: 'OpenStax — Principles of Finance', u: 'https://openstax.org/details/books/principles-finance', k: 'scholarly' },
    osEcon: { t: 'OpenStax — Principles of Economics 3e', u: 'https://openstax.org/details/books/principles-economics-3e', k: 'scholarly' },
    osPsy: { t: 'OpenStax — Psychology 2e', u: 'https://openstax.org/details/books/psychology-2e', k: 'scholarly' },
    osSoc: { t: 'OpenStax — Introduction to Sociology 3e', u: 'https://openstax.org/details/books/introduction-sociology-3e', k: 'scholarly' },
    osBio: { t: 'OpenStax — Biology 2e', u: 'https://openstax.org/details/books/biology-2e', k: 'scholarly' },
    osChem: { t: 'OpenStax — Chemistry 2e', u: 'https://openstax.org/details/books/chemistry-2e', k: 'scholarly' },
    osStat: { t: 'OpenStax — Introductory Statistics', u: 'https://openstax.org/details/books/introductory-statistics', k: 'scholarly' },
    osUS: { t: 'OpenStax — U.S. History', u: 'https://openstax.org/details/books/us-history', k: 'scholarly' },
    nist: { t: 'NIST — Cybersecurity Framework', u: 'https://www.nist.gov/cyberframework', k: 'scholarly' },
    statpearls: { t: 'StatPearls (NCBI Bookshelf)', u: 'https://www.ncbi.nlm.nih.gov/books/NBK430685/', k: 'scholarly' },
    bjs: { t: 'Bureau of Justice Statistics', u: 'https://bjs.ojp.gov/', k: 'scholarly' },
    ed: { t: 'U.S. Department of Education — IDEA', u: 'https://sites.ed.gov/idea/', k: 'scholarly' }
  };

  /* =====================================================================
     1. CLASSIFICATION
     ===================================================================== */

  /* Course-code prefixes are the strongest signal available. Ordered longest
     first so ACCT matches before ACC, PSYC before PSY, and so on. */
  var PREFIX = [
    ['ACCT', 'accounting'], ['ACC', 'accounting'], ['AC', 'accounting'],
    ['FIN', 'finance'], ['FI', 'finance'],
    ['ECON', 'economics'], ['ECO', 'economics'],
    ['MKTG', 'marketing'], ['MKT', 'marketing'], ['MAR', 'marketing'],
    ['MGMT', 'business'], ['MGT', 'business'], ['BUSN', 'business'], ['BUS', 'business'],
    ['HRM', 'business'], ['HRMN', 'business'], ['LEAD', 'business'], ['SCM', 'business'],
    ['ENTR', 'business'], ['ORG', 'business'], ['OPS', 'business'],
    ['MIS', 'infosystems'], ['CIS', 'infosystems'], ['ISM', 'infosystems'],
    ['ITEC', 'infosystems'], ['ITM', 'infosystems'], ['IST', 'infosystems'], ['IT', 'infosystems'],
    ['CSIS', 'infosystems'], ['CSCI', 'infosystems'], ['COSC', 'infosystems'],
    ['COMP', 'infosystems'], ['CPSC', 'infosystems'], ['CS', 'infosystems'],
    ['DATA', 'infosystems'], ['SEC', 'infosystems'], ['NETW', 'infosystems'], ['CYB', 'infosystems'],
    ['PSYC', 'psychology'], ['PSY', 'psychology'],
    ['SOCI', 'sociology'], ['SOC', 'sociology'], ['ANTH', 'sociology'],
    ['NURS', 'nursing'], ['NUR', 'nursing'], ['NR', 'nursing'], ['HCA', 'nursing'],
    ['HLTH', 'nursing'], ['HIM', 'nursing'], ['MED', 'nursing'], ['PHAR', 'nursing'],
    ['BIOS', 'biology'], ['BIOL', 'biology'], ['BIO', 'biology'], ['ANAT', 'biology'], ['MICR', 'biology'],
    ['CHEM', 'chemistry'], ['CHM', 'chemistry'],
    ['PHYS', 'physics'], ['PHY', 'physics'],
    ['STAT', 'statistics'], ['STA', 'statistics'],
    ['MATH', 'math'], ['MTH', 'math'], ['ALG', 'math'], ['CALC', 'math'], ['QNT', 'math'],
    ['ENGL', 'english'], ['ENG', 'english'], ['WRTG', 'english'], ['WRT', 'english'],
    ['COMM', 'english'], ['COM', 'english'], ['LIT', 'english'], ['RHET', 'english'],
    ['HIST', 'history'], ['HIS', 'history'], ['HUMN', 'history'], ['HUM', 'history'],
    ['PHIL', 'history'], ['POLS', 'history'], ['POL', 'history'], ['GOVT', 'history'],
    ['CRJ', 'criminal'], ['CJUS', 'criminal'], ['CJ', 'criminal'], ['LAW', 'criminal'], ['LEGL', 'criminal'],
    ['EDUC', 'education'], ['EDU', 'education'], ['TCH', 'education'], ['ECE', 'education'],
    ['ETHC', 'ethics'], ['ETH', 'ethics'], ['RELI', 'ethics'], ['REL', 'ethics']
  ];

  var TITLE_HINTS = [
    [/information system|database|data mining|data warehouse|sql|network|cyber|security|programming|software|computer|coding|python|java|analytics|cloud|informatic/i, 'infosystems'],
    [/account|ledger|audit|taxation|bookkeep/i, 'accounting'],
    [/finance|financial management|investment|capital budget|corporate finance/i, 'finance'],
    [/econom|macro|microecon/i, 'economics'],
    [/marketing|consumer behav|brand|advertis|promotion/i, 'marketing'],
    [/management|leadership|organiz|organis|human resource|supply chain|operations|strategy|entrepreneur|project manage/i, 'business'],
    [/psycholog|cognit|behavio(u)?r|counsel|mental health|developmental/i, 'psychology'],
    [/sociolog|anthropolog|social problem|cultur|diversity|gender stud/i, 'sociology'],
    [/nurs|pathophysiolog|pharmacolog|patient|clinical|health care|healthcare|anatomy|physiolog|medical|dosage/i, 'nursing'],
    [/biolog|microbiolog|genetic|ecolog|cell/i, 'biology'],
    [/chemistr|organic chem|biochem/i, 'chemistry'],
    [/physic|mechanic|thermodynam/i, 'physics'],
    [/statistic|biostat|quantitative analysis|probability/i, 'statistics'],
    [/algebra|calculus|trigonometr|precalc|discrete math|mathematic/i, 'math'],
    [/english|composition|writing|literature|rhetoric|communication|speech/i, 'english'],
    [/history|civilization|civilisation|philosoph|government|political|humanities|world religion/i, 'history'],
    [/criminal|justice|policing|corrections|forensic|law|legal|paralegal/i, 'criminal'],
    [/education|teaching|curriculum|instructional|classroom|literacy/i, 'education'],
    [/ethic|religio|theolog|bible|moral/i, 'ethics']
  ];

  function classify(title, tags, type) {
    var t = String(title || '');

    /* Course code, e.g. "MIS561 Data Mining" or "NR-503 Population Health" */
    var m = /\b([A-Za-z]{2,5})[\s\-_]?\d{3}\b/.exec(t) || /\b([A-Za-z]{2,5})[\s\-_]?\d{2,4}\b/.exec(t);
    if (m) {
      var pre = m[1].toUpperCase();
      for (var i = 0; i < PREFIX.length; i++) {
        if (pre === PREFIX[i][0]) return PREFIX[i][1];
      }
    }

    var hay = t + ' ' + (tags || '') + ' ' + (type || '');
    for (var j = 0; j < TITLE_HINTS.length; j++) {
      if (TITLE_HINTS[j][0].test(hay)) return TITLE_HINTS[j][1];
    }
    return 'general';
  }

  /* =====================================================================
     2. SUBJECT CONTENT
     Each entry: { t: term/stem, d: correct, wrong: [3 distractors], why?, refs }
     ===================================================================== */

  function T(term, def, wrong, extra) {
    return { t: term, d: def, wrong: wrong, extra: extra || '' };
  }

  var SUBJECTS = {

    infosystems: {
      label: 'Information Systems & Computing',
      sections: [
        { id: 'concepts', name: 'Systems & Data Concepts', weight: 30 },
        { id: 'applied', name: 'Design & Implementation', weight: 40 },
        { id: 'analysis', name: 'Governance, Security & Analysis', weight: 30 }
      ],
      refs: [REF.nist],
      pool: [
        T('Normalisation in relational database design',
          'organising tables to reduce redundancy and prevent update, insertion and deletion anomalies',
          ['combining tables so that fewer joins are required at query time',
           'converting all numeric values to a common scale before analysis',
           'encrypting stored data so it cannot be read without a key'],
          'Third normal form is the usual practical target: every non-key attribute depends on the key, the whole key, and nothing but the key. Denormalisation is a deliberate reversal for read performance, not a design failure.'),
        T('A primary key',
          'a column or set of columns that uniquely identifies each row in a table',
          ['a column that references the primary key of another table',
           'the column a table is most often sorted by',
           'the first column defined when the table is created'],
          'A foreign key is the one that references another table. Primary keys must be unique and non-null; that constraint is what makes joins reliable.'),
        T('An ERD (entity-relationship diagram)',
          'a model showing entities, their attributes, and the relationships between them',
          ['a chart of the physical servers on which a database runs',
           'a diagram of the sequence in which queries execute',
           'a map of user permissions across a system'],
          'ERDs sit at the logical design stage — before physical implementation and after requirements gathering.'),
        T('OLTP versus OLAP',
          'OLTP handles many small transactional writes; OLAP handles complex analytical reads over historical data',
          ['OLTP is used online while OLAP runs only on local machines',
           'OLTP stores unstructured data while OLAP stores relational tables',
           'OLTP is a hardware standard and OLAP a software one'],
          'This distinction is why data warehouses exist: running heavy analytical queries against a live transactional system degrades the system users depend on.'),
        T('ETL in a data warehouse context',
          'Extract, Transform, Load — pulling data from sources, reshaping it, then loading it into the warehouse',
          ['Encrypt, Transmit, Log — the standard secure transfer sequence',
           'Evaluate, Test, Launch — the phases of system deployment',
           'Enter, Track, List — the stages of transaction processing'],
          'ELT reverses the last two steps, loading raw data first and transforming inside the warehouse — common with cloud platforms that have cheap compute.'),
        T('The CIA triad in information security',
          'Confidentiality, Integrity and Availability — the three goals every control serves',
          ['Control, Isolation and Authentication',
           'Compliance, Investigation and Audit',
           'Classification, Identification and Access'],
          'Useful diagnostically: encryption serves confidentiality, hashing and checksums serve integrity, redundancy and backups serve availability. A ransomware attack breaches availability first.'),
        T('The systems development life cycle (SDLC)',
          'the structured phases of planning, analysis, design, implementation and maintenance for building a system',
          ['the period over which hardware is depreciated for accounting purposes',
           'the sequence in which a database processes a transaction',
           'the schedule on which security patches are released'],
          'Waterfall runs the phases once in order; Agile iterates through them repeatedly in short cycles. Both cover the same activities.'),
        T('A data dictionary',
          'documentation defining each data element — its name, type, allowed values and meaning',
          ['a lookup table translating codes into display labels at runtime',
           'the index a database uses to speed up searches',
           'a backup copy of the database schema'],
          'It is the reference that keeps meaning consistent when several analysts touch the same warehouse.'),
        T('Business intelligence',
          'the technologies and practices for analysing business data to support decision-making',
          ['the collection of competitor information through market research',
           'the automation of routine business transactions',
           'the measurement of employee performance against targets'],
          'BI is descriptive and diagnostic — what happened and why. Predictive and prescriptive analytics extend it to what will happen and what to do.'),
        T('Data mining',
          'discovering previously unknown patterns and relationships in large datasets',
          ['retrieving specific records that match a known query',
           'physically transferring data between storage systems',
           'removing duplicate records from a database'],
          'The distinction that gets tested: querying answers a question you already have; mining surfaces patterns you did not know to ask about. Classification, clustering and association rules are the core techniques.'),
        T('A foreign key constraint',
          'a rule requiring that a value in one table must match an existing primary key value in another',
          ['a limit on how many rows a table may contain',
           'a requirement that a column contain only unique values',
           'a restriction preventing a table from being queried by outside users'],
          'This is what enforces referential integrity — it prevents orphaned records, such as an order pointing at a customer who does not exist.'),
        T('Cloud SaaS (Software as a Service)',
          'complete applications delivered over the internet, with the provider managing everything beneath',
          ['raw virtual machines and storage the customer configures themselves',
           'a development platform on which customers deploy their own code',
           'private hardware hosted in the customer\'s own data centre'],
          'The stack, from least to most managed by the provider: IaaS (infrastructure), PaaS (platform), SaaS (software). Salesforce is SaaS; AWS EC2 is IaaS.'),
        T('Two-factor authentication',
          'requiring credentials from two different categories — something you know, have, or are',
          ['requiring two separate passwords for the same account',
           'verifying a user against two different databases',
           'encrypting credentials twice before transmission'],
          'Two passwords are the same category, so they are not two factors. A password plus a phone code is; a password plus a fingerprint is.'),
        T('A relational join',
          'combining rows from two tables based on a related column between them',
          ['appending the rows of one table to the end of another',
           'merging duplicate rows within a single table',
           'copying a table into a new database'],
          'An INNER JOIN keeps only matching rows; a LEFT JOIN keeps all rows from the left table regardless of a match. Choosing the wrong one silently drops or duplicates data.')
      ]
    },

    business: {
      label: 'Business & Management',
      sections: [
        { id: 'concepts', name: 'Management Theory', weight: 30 },
        { id: 'applied', name: 'Strategy & Operations', weight: 40 },
        { id: 'analysis', name: 'Organisational Analysis', weight: 30 }
      ],
      refs: [REF.osMgmt, REF.osBiz],
      pool: [
        T('A SWOT analysis',
          'a framework assessing internal Strengths and Weaknesses against external Opportunities and Threats',
          ['a method of forecasting cash flow over four quarters',
           'a model ranking suppliers by cost, quality and reliability',
           'a technique for scheduling project tasks against dependencies'],
          'The split that gets marked: strengths and weaknesses are internal and within the firm\'s control; opportunities and threats are external. Misplacing one is the most common error in written answers.'),
        T('Porter\'s five forces',
          'a model analysing industry attractiveness through rivalry, new entrants, substitutes, and buyer and supplier power',
          ['a framework for classifying employees by performance and potential',
           'a sequence of five stages every product passes through',
           'the five functions of management identified by Fayol'],
          'It analyses the industry, not the firm — a common confusion with SWOT, which analyses the firm.'),
        T('Maslow\'s hierarchy of needs',
          'a motivation theory ordering needs from physiological through safety, belonging and esteem to self-actualisation',
          ['a model ranking organisational goals by financial priority',
           'a classification of leadership styles by degree of control',
           'a framework for sequencing strategic decisions'],
          'Herzberg\'s two-factor theory is the usual alternative: hygiene factors prevent dissatisfaction, motivators create satisfaction — and pay is a hygiene factor, which is what makes it examinable.'),
        T('Transformational leadership',
          'inspiring followers to exceed expectations by appealing to shared vision and higher purpose',
          ['exchanging rewards for performance through clear contingent incentives',
           'leaving decisions entirely to subordinates without direction',
           'enforcing standards through close supervision and correction'],
          'Transactional leadership is the reward-exchange one. Laissez-faire is the hands-off one. Exam items usually describe a scenario and ask you to name the style.'),
        T('A stakeholder',
          'any party affected by or able to affect an organisation\'s actions',
          ['only those parties holding an equity share in the organisation',
           'the members of the board of directors',
           'customers who have made a purchase in the last year'],
          'Shareholders are a subset of stakeholders. The wider definition includes employees, suppliers, communities and regulators, and that breadth is the point of the concept.'),
        T('The difference between efficiency and effectiveness',
          'efficiency is doing things with minimum resource; effectiveness is doing the right things to achieve the goal',
          ['efficiency applies to individuals and effectiveness to organisations',
           'efficiency is measured financially and effectiveness operationally',
           'they are interchangeable terms for productivity'],
          'Drucker\'s framing: it is possible to be highly efficient at something that should not be done at all. Exam scenarios often describe exactly that.'),
        T('A SMART objective',
          'one that is Specific, Measurable, Achievable, Relevant and Time-bound',
          ['one aligned to Strategy, Market, Assets, Revenue and Team',
           'a goal set jointly by a manager and subordinate',
           'an objective reviewed at the end of each financial year'],
          '"Increase sales" fails on every letter. "Increase Q3 online sales by 8% by 30 September" passes on all five.'),
        T('Span of control',
          'the number of subordinates reporting directly to one manager',
          ['the geographic area over which a firm operates',
           'the range of decisions a manager is authorised to make',
           'the length of time a manager holds a position'],
          'A wide span produces a flat structure with fewer layers; a narrow span produces a tall one with more supervision and slower communication.'),
        T('The four functions of management',
          'planning, organising, leading and controlling',
          ['forecasting, budgeting, staffing and reporting',
           'analysing, deciding, delegating and reviewing',
           'hiring, training, motivating and appraising'],
          'Controlling here means monitoring performance against plan and correcting — not exercising power over people.'),
        T('Corporate social responsibility',
          'an organisation\'s obligation to act in the wider interests of society beyond legal and profit requirements',
          ['the legal duty of directors to maximise shareholder returns',
           'a firm\'s compliance programme for employment regulation',
           'the reporting of financial results to the public'],
          'Carroll\'s pyramid layers it: economic, legal, ethical, then philanthropic — with each level resting on the one below.'),
        T('A vertical merger',
          'a combination of firms at different stages of the same supply chain',
          ['a combination of two direct competitors in the same market',
           'a combination of firms in wholly unrelated industries',
           'the purchase of a minority stake in a supplier'],
          'A manufacturer buying its component supplier is vertical; buying a rival manufacturer is horizontal; buying an unrelated business is conglomerate.'),
        T('Just-in-time inventory',
          'receiving materials only as needed, minimising holding costs and stock on hand',
          ['maintaining large safety stocks to guarantee availability',
           'ordering the economically optimal quantity once per year',
           'holding inventory at the point of sale rather than in a warehouse'],
          'The trade-off is real and examinable: JIT cuts carrying cost but removes the buffer against supply disruption, as recent global shortages showed.')
      ]
    },

    accounting: {
      label: 'Accounting',
      sections: [
        { id: 'concepts', name: 'Principles & Framework', weight: 30 },
        { id: 'applied', name: 'Recording & Reporting', weight: 40 },
        { id: 'analysis', name: 'Analysis & Interpretation', weight: 30 }
      ],
      refs: [REF.osAcc],
      pool: [
        T('The accounting equation',
          'Assets = Liabilities + Owner\'s Equity',
          ['Assets = Revenue − Expenses',
           'Assets + Liabilities = Owner\'s Equity',
           'Revenue = Assets − Liabilities'],
          'Every transaction keeps this in balance. If it does not, an entry is missing or on the wrong side.'),
        T('The accrual basis of accounting',
          'recognising revenue when earned and expenses when incurred, regardless of when cash moves',
          ['recognising revenue and expenses only when cash is received or paid',
           'recording only transactions above a set materiality threshold',
           'reporting results annually rather than quarterly'],
          'The cash basis is the alternative. Accrual gives a truer picture of a period\'s performance, which is why GAAP requires it.'),
        T('A debit',
          'an entry on the left side of an account, which increases assets and expenses',
          ['an entry that always decreases an account balance',
           'a reduction in the amount a customer owes',
           'a withdrawal of cash by the owner'],
          'The rule that resolves most errors: debits increase assets, expenses and drawings; credits increase liabilities, equity and revenue. Debit does not mean "bad" or "decrease".'),
        T('Depreciation',
          'the systematic allocation of an asset\'s cost over its useful life',
          ['the fall in an asset\'s market value between two dates',
           'the cash set aside to replace an asset when it wears out',
           'the loss recognised when an asset is sold below book value'],
          'It is an allocation of cost, not a valuation and not a cash flow. That is why it is added back in the operating section of a cash-flow statement.'),
        T('A current asset',
          'an asset expected to be converted to cash or used within one year',
          ['an asset the business currently owns outright with no financing',
           'an asset recorded at its present market value',
           'an asset purchased in the current accounting period'],
          'Cash, accounts receivable, inventory and prepaid expenses. Property and equipment are non-current regardless of when they were bought.'),
        T('The matching principle',
          'expenses are recorded in the same period as the revenues they helped generate',
          ['assets must be matched by an equal value of liabilities',
           'each debit entry must be matched by an equal credit entry',
           'budgeted figures must be matched against actual results'],
          'Cost of goods sold is the clearest illustration — recognised when the sale is recognised, not when the inventory was purchased.'),
        T('Gross profit',
          'net sales less cost of goods sold',
          ['net sales less all operating expenses',
           'net income before tax but after interest',
           'total revenue from all sources before any deductions'],
          'Gross profit sits above operating expenses; operating income is after them; net income is after interest and tax. Confusing the tiers is the most common error on income-statement questions.'),
        T('The current ratio',
          'current assets divided by current liabilities',
          ['total assets divided by total liabilities',
           'cash divided by current liabilities',
           'current liabilities divided by equity'],
          'It measures short-term liquidity. The quick ratio is stricter, excluding inventory, because inventory may not convert to cash quickly.'),
        T('An adjusting entry',
          'an entry made at period end to record revenues earned or expenses incurred but not yet recorded',
          ['a correction of an error found in a previous entry',
           'an entry that closes revenue and expense accounts to equity',
           'an entry recording a transaction with a related party'],
          'Accrued revenue, accrued expenses, prepaid expenses and unearned revenue are the four types. Closing entries are a separate, later step.'),
        T('Retained earnings',
          'cumulative net income not distributed to owners as dividends',
          ['the cash balance the business holds at year end',
           'profit earned in the current period only',
           'the original amount invested by shareholders'],
          'It is an equity account, not cash. A company can hold large retained earnings and still be short of cash — a distinction exam questions probe directly.'),
        T('FIFO inventory costing',
          'assuming the earliest units purchased are the first ones sold',
          ['assuming the most recently purchased units are sold first',
           'valuing all inventory at its current replacement cost',
           'assigning the average cost of all units to each sale'],
          'In a period of rising prices FIFO leaves newer, higher costs in ending inventory and reports higher net income than LIFO. That directional effect is what gets tested.')
      ]
    },

    finance: {
      label: 'Finance',
      sections: [
        { id: 'concepts', name: 'Financial Concepts', weight: 30 },
        { id: 'applied', name: 'Valuation & Capital Budgeting', weight: 40 },
        { id: 'analysis', name: 'Risk & Financial Analysis', weight: 30 }
      ],
      refs: [REF.osFin],
      pool: [
        T('The time value of money',
          'the principle that a sum available today is worth more than the same sum in the future',
          ['the tendency of money to lose value through inflation alone',
           'the requirement that financial statements be prepared annually',
           'the relationship between a currency\'s value and interest rates abroad'],
          'It holds because money available now can be invested to earn a return. Every discounting technique — NPV, present value, bond pricing — rests on it.'),
        T('Net present value (NPV)',
          'the present value of future cash inflows less the initial investment',
          ['the total of all future cash flows without discounting',
           'the accounting profit expected over a project\'s life',
           'the market value of a firm less its book value'],
          'Decision rule: accept if NPV is positive. NPV is preferred to IRR because it handles unconventional cash flows and mutually exclusive projects correctly.'),
        T('Diversification in portfolio theory',
          'holding assets whose returns are imperfectly correlated in order to reduce total risk',
          ['spreading investments across as many assets as possible regardless of correlation',
           'holding only assets with the highest historical returns',
           'shifting an entire portfolio into cash during downturns'],
          'It removes unsystematic (firm-specific) risk. Systematic market risk cannot be diversified away, which is precisely what beta measures.'),
        T('Beta in the capital asset pricing model',
          'a measure of an asset\'s sensitivity to movements in the overall market',
          ['the total volatility of an asset\'s returns',
           'the return an asset earns above the risk-free rate',
           'the proportion of a firm financed by debt'],
          'A beta of 1 moves with the market; above 1 amplifies it; below 1 dampens it. Total volatility is standard deviation, a different measure.'),
        T('The weighted average cost of capital (WACC)',
          'the blended after-tax cost of a firm\'s debt and equity, weighted by their proportions',
          ['the interest rate a firm pays on its newest borrowing',
           'the return shareholders received in the past year',
           'the average interest rate available in the wider market'],
          'It is the usual discount rate for project appraisal. Debt is cheaper than equity partly because interest is tax-deductible — the "tax shield".'),
        T('Working capital',
          'current assets less current liabilities',
          ['total assets less total liabilities',
           'the cash a firm holds at the start of a period',
           'the capital raised from shareholders at incorporation'],
          'Negative working capital signals possible short-term liquidity strain, though some retail models run it deliberately by collecting from customers before paying suppliers.'),
        T('Financial leverage',
          'the use of borrowed funds to increase the potential return on equity',
          ['the ability of a firm to influence prices in its market',
           'the ratio of fixed to variable operating costs',
           'the practice of holding assets in more than one currency'],
          'Leverage magnifies both gains and losses. Operating leverage is the fixed-versus-variable-cost one, a separate concept that is frequently confused with it.'),
        T('A bond\'s relationship to interest rates',
          'bond prices move inversely to market interest rates',
          ['bond prices rise when interest rates rise',
           'bond prices are unaffected by interest rate changes',
           'bond prices move with rates only after maturity'],
          'When rates rise, existing bonds paying lower coupons become less attractive, so their price falls until their yield matches the market. Longer maturities are more sensitive.')
      ]
    },

    economics: {
      label: 'Economics',
      sections: [
        { id: 'concepts', name: 'Core Principles', weight: 30 },
        { id: 'applied', name: 'Markets & Policy', weight: 40 },
        { id: 'analysis', name: 'Macroeconomic Analysis', weight: 30 }
      ],
      refs: [REF.osEcon],
      pool: [
        T('Opportunity cost',
          'the value of the next-best alternative given up when a choice is made',
          ['the total money spent on a chosen option',
           'the loss incurred when an investment underperforms',
           'the cost of producing one additional unit'],
          'It includes forgone alternatives, not just cash outlay — which is why the cost of a degree includes the salary not earned while studying.'),
        T('The law of demand',
          'as price rises, quantity demanded falls, all else equal',
          ['as price rises, quantity supplied falls',
           'as income rises, demand for all goods rises',
           'as supply rises, price rises proportionally'],
          'Note the distinction the exam tests: a price change moves you <em>along</em> the demand curve; a change in income, tastes or substitutes <em>shifts</em> the whole curve.'),
        T('Gross domestic product',
          'the total market value of final goods and services produced within a country in a period',
          ['the total income earned by a country\'s citizens wherever they live',
           'the total value of a country\'s exports minus imports',
           'the total wealth held by households in a country'],
          'GNP is the citizens-wherever-they-live measure. GDP counts only <em>final</em> goods, to avoid double-counting intermediate inputs.'),
        T('Inflation',
          'a sustained rise in the general price level, reducing the purchasing power of money',
          ['a rise in the price of a single important commodity',
           'an increase in the money supply regardless of prices',
           'a fall in the exchange rate against other currencies'],
          'Demand-pull comes from excess demand; cost-push from rising input costs. The distinction matters because the appropriate policy response differs.'),
        T('Fiscal policy',
          'government use of taxation and spending to influence the economy',
          ['central bank control of interest rates and money supply',
           'government regulation of prices in key industries',
           'management of the exchange rate through currency reserves'],
          'Monetary policy is the central bank one. Expansionary fiscal policy means more spending or lower taxes; contractionary is the reverse.'),
        T('A price ceiling set below equilibrium',
          'creates a shortage, because quantity demanded exceeds quantity supplied',
          ['creates a surplus of unsold goods',
           'has no effect because markets adjust automatically',
           'raises the market price to the ceiling level'],
          'Rent control is the standard example. A price <em>floor</em> above equilibrium — minimum wage, farm supports — produces the opposite, a surplus.'),
        T('Elastic demand',
          'quantity demanded responds more than proportionally to a change in price',
          ['quantity demanded barely changes when price changes',
           'demand rises steadily over time regardless of price',
           'the demand curve is vertical'],
          'Goods with close substitutes and luxuries tend to be elastic; necessities and addictive goods inelastic. If demand is elastic, raising price lowers total revenue.'),
        T('Comparative advantage',
          'the ability to produce a good at a lower opportunity cost than another producer',
          ['the ability to produce more of a good using the same resources',
           'possession of a natural resource another country lacks',
           'the ability to sell a good at a lower price than competitors'],
          'Absolute advantage is the produce-more one. Ricardo\'s insight is that trade benefits both parties even when one holds an absolute advantage in everything.')
      ]
    },

    marketing: {
      label: 'Marketing',
      sections: [
        { id: 'concepts', name: 'Marketing Foundations', weight: 30 },
        { id: 'applied', name: 'Strategy & Mix', weight: 40 },
        { id: 'analysis', name: 'Consumer & Market Analysis', weight: 30 }
      ],
      refs: [REF.osMkt],
      pool: [
        T('The four Ps of the marketing mix',
          'Product, Price, Place and Promotion',
          ['Planning, Positioning, Packaging and Profit',
           'People, Process, Performance and Physical evidence',
           'Purpose, Personalisation, Platform and Partnership'],
          'Services marketing extends this to seven, adding People, Process and Physical evidence — worth knowing if the course covers services.'),
        T('Market segmentation',
          'dividing a broad market into groups with similar needs or characteristics',
          ['choosing which single market a firm will compete in',
           'establishing how a brand is perceived relative to competitors',
           'setting different prices in different geographic regions'],
          'The sequence is segmentation → targeting → positioning. Segmentation bases are demographic, geographic, psychographic and behavioural.'),
        T('Positioning',
          'the place a brand occupies in the customer\'s mind relative to competitors',
          ['the physical location of a product on a retail shelf',
           'the stage a product has reached in its life cycle',
           'the rank of a firm by market share'],
          'A positioning statement names the target segment, the frame of reference and the point of difference. Volvo and safety is the textbook case.'),
        T('A product life cycle',
          'the stages of introduction, growth, maturity and decline that a product passes through',
          ['the time from raw material sourcing to final disposal',
           'the warranty period a manufacturer offers',
           'the schedule on which a product is restocked'],
          'Marketing priorities shift by stage: build awareness at introduction, defend share at maturity, harvest or divest in decline.'),
        T('Penetration pricing',
          'setting a low initial price to gain market share quickly',
          ['setting a high initial price to maximise early margins',
           'matching the price of the nearest competitor',
           'pricing at cost plus a fixed percentage'],
          'Price skimming is the high-initial-price strategy. Penetration suits price-sensitive markets with economies of scale; skimming suits innovative products with early adopters.'),
        T('Brand equity',
          'the commercial value that derives from customer perception of the brand',
          ['the total advertising spend accumulated over a brand\'s life',
           'the legal protection a trademark provides',
           'the shelf space a brand occupies across retailers'],
          'It shows up as pricing power, customer loyalty and lower acquisition cost — which is why it appears on balance sheets after acquisitions.'),
        T('The AIDA model',
          'Attention, Interest, Desire, Action — stages a buyer passes through toward purchase',
          ['Analyse, Implement, Deliver, Assess — the campaign management cycle',
           'Awareness, Involvement, Decision, Advocacy — the loyalty ladder',
           'Acquire, Integrate, Develop, Advance — the customer growth path'],
          'It is a hierarchy-of-effects model: communication objectives differ at each stage, so a campaign aimed at Attention is measured differently from one aimed at Action.')
      ]
    },

    psychology: {
      label: 'Psychology',
      sections: [
        { id: 'concepts', name: 'Theories & Perspectives', weight: 30 },
        { id: 'applied', name: 'Development & Behaviour', weight: 40 },
        { id: 'analysis', name: 'Research Methods & Analysis', weight: 30 }
      ],
      refs: [REF.osPsy],
      pool: [
        T('Classical conditioning',
          'learning in which a neutral stimulus comes to trigger a response after pairing with an unconditioned stimulus',
          ['learning in which behaviour is shaped by its consequences',
           'learning that occurs by watching and imitating others',
           'learning that results from sudden insight rather than practice'],
          'Pavlov\'s dogs. Operant conditioning is the consequences one (Skinner); observational learning is Bandura. Classical involves involuntary responses, operant voluntary behaviour.'),
        T('Negative reinforcement',
          'removing an unpleasant stimulus in order to increase a behaviour',
          ['applying an unpleasant consequence to decrease a behaviour',
           'withholding a reward to decrease a behaviour',
           'ignoring a behaviour until it stops'],
          'The most-missed term in the subject. <strong>Reinforcement always increases behaviour</strong>; punishment decreases it. "Negative" means something is removed, not that it is unpleasant. Taking painkillers to remove pain is negative reinforcement.'),
        T('Piaget\'s concrete operational stage',
          'roughly ages 7 to 11, when children think logically about concrete events and grasp conservation',
          ['birth to age 2, when knowledge comes through senses and movement',
           'ages 2 to 7, when thinking is egocentric and symbolic',
           'age 12 onward, when abstract and hypothetical reasoning develops'],
          'The four in order: sensorimotor, preoperational, concrete operational, formal operational. Conservation — knowing quantity survives a change in appearance — is the marker of the concrete stage.'),
        T('The independent variable',
          'the variable the researcher manipulates to observe its effect',
          ['the variable that is measured as an outcome',
           'a variable held constant across all conditions',
           'an unmeasured variable that distorts the relationship'],
          'The dependent variable is measured; a confounding variable is the unmeasured distorter. Getting these the wrong way round is the commonest methods error.'),
        T('Cognitive dissonance',
          'the psychological discomfort of holding inconsistent beliefs or acting against one\'s beliefs',
          ['the inability to recall information encoded under stress',
           'the tendency to seek information confirming existing beliefs',
           'the decline in cognitive performance with divided attention'],
          'Festinger. Resolution comes by changing the belief, changing the behaviour, or adding a justification — the last being why people rationalise.'),
        T('Correlation versus causation',
          'a correlation shows two variables move together but cannot by itself establish that one causes the other',
          ['a strong correlation always indicates a causal relationship',
           'correlation applies to experiments and causation to surveys',
           'causation can only be established through correlation coefficients'],
          'Only a controlled experiment with random assignment supports causal claims. A third variable, or reverse causation, can produce correlation without cause.'),
        T('The fundamental attribution error',
          'overestimating personality and underestimating situation when explaining others\' behaviour',
          ['attributing one\'s own failures to circumstances and successes to ability',
           'assuming others share one\'s own opinions and preferences',
           'judging probability by how easily examples come to mind'],
          'The second option is the self-serving bias, the third the false consensus effect, the fourth the availability heuristic — all common distractors here.'),
        T('Informed consent in research ethics',
          'participants must be told the nature, risks and purpose of a study and agree voluntarily before taking part',
          ['participants must be paid fairly for their time',
           'researchers must obtain approval from their department head',
           'data must be anonymised before publication'],
          'Along with the right to withdraw, protection from harm, confidentiality and debriefing, this is one of the core IRB requirements.')
      ]
    },

    sociology: {
      label: 'Sociology',
      sections: [
        { id: 'concepts', name: 'Theoretical Perspectives', weight: 30 },
        { id: 'applied', name: 'Institutions & Inequality', weight: 40 },
        { id: 'analysis', name: 'Research & Analysis', weight: 30 }
      ],
      refs: [REF.osSoc],
      pool: [
        T('The functionalist perspective',
          'viewing society as interrelated parts each serving a function that maintains stability',
          ['viewing society as an arena of competition between groups over scarce resources',
           'focusing on the meanings people create through everyday interaction',
           'analysing society primarily through gendered power relations'],
          'The three major perspectives are functionalism, conflict theory and symbolic interactionism. The second option is conflict theory, the third interactionism.'),
        T('The sociological imagination',
          'the ability to see personal experience in the context of wider social forces and history',
          ['the capacity to predict how societies will change over time',
           'the use of statistical models to explain social behaviour',
           'the tendency to interpret other cultures through one\'s own values'],
          'C. Wright Mills — connecting "personal troubles" to "public issues". Unemployment as a personal failing versus unemployment as a structural feature is the canonical illustration.'),
        T('Ethnocentrism',
          'judging another culture by the standards of one\'s own',
          ['understanding a culture on its own terms without judgement',
           'the belief that all cultural practices are equally valid',
           'the gradual blending of two cultures in contact'],
          'Cultural relativism is the opposite stance, and it is the methodological norm in the discipline.'),
        T('Social stratification',
          'the hierarchical arrangement of groups in society by access to resources and status',
          ['the movement of individuals between social positions',
           'the division of labour within a single organisation',
           'the geographic clustering of similar households'],
          'Movement between positions is social <em>mobility</em>. Stratification systems range from closed (caste) to relatively open (class).'),
        T('A norm',
          'a shared expectation about how members of a group should behave',
          ['a shared belief about what is desirable or good',
           'a statistical average of behaviour in a population',
           'a formally codified law enforced by the state'],
          'Values are the beliefs about what is desirable; norms are the behavioural rules that follow from them. Folkways, mores and laws are norms of increasing sanction.'),
        T('A qualitative research method',
          'one producing non-numerical data such as interviews, ethnography or content analysis',
          ['one relying on large representative samples and statistical inference',
           'one conducted only in laboratory settings',
           'one that cannot produce generalisable findings'],
          'Qualitative work trades breadth for depth. It is not inherently less rigorous — the criteria for quality simply differ.')
      ]
    },

    nursing: {
      label: 'Nursing & Health Sciences',
      sections: [
        { id: 'concepts', name: 'Foundations & Pathophysiology', weight: 30 },
        { id: 'applied', name: 'Clinical Application', weight: 40 },
        { id: 'analysis', name: 'Clinical Judgement', weight: 30 }
      ],
      refs: [REF.statpearls],
      pool: [
        T('The nursing process',
          'Assessment, Diagnosis, Planning, Implementation and Evaluation (ADPIE)',
          ['Admission, Diagnosis, Prescription, Intervention, Discharge',
           'Assess, Document, Perform, Inform, Escalate',
           'Analyse, Decide, Plan, Instruct, Evaluate'],
          'Assessment always comes first, and evaluation feeds back into reassessment. On "what should the nurse do first" items, the answer is usually assessment unless an airway, breathing or circulation problem demands action.'),
        T('Evidence-based practice',
          'integrating best available research evidence with clinical expertise and patient values',
          ['following institutional policy exactly as written in all cases',
           'relying on the most recent published study regardless of quality',
           'basing care on the consensus of experienced colleagues'],
          'All three legs are required. A systematic review of randomised trials sits at the top of the evidence hierarchy; expert opinion at the bottom.'),
        T('An objective assessment finding',
          'data the nurse can observe, measure or verify directly',
          ['what the patient reports about their own symptoms',
           'the nurse\'s interpretation of the patient\'s condition',
           'information obtained from a family member'],
          'Subjective data is what the patient tells you — "I feel dizzy". Objective is what you can measure — a blood pressure of 88/50. Pain is subjective by definition, which is why self-report is the standard.'),
        T('Standard precautions',
          'infection-control measures applied to every patient regardless of diagnosis',
          ['additional measures used only for patients with known infections',
           'precautions required only in operating theatres',
           'measures applied only when visible blood is present'],
          'They assume all blood and body fluids are potentially infectious. Transmission-based precautions — contact, droplet, airborne — are layered on top for specific organisms.'),
        T('Informed consent for a procedure',
          'the provider explains the procedure, risks, benefits and alternatives; the nurse verifies understanding and witnesses the signature',
          ['the nurse explains the procedure and obtains the signature',
           'consent is implied whenever a patient attends for treatment',
           'a family member may consent on behalf of any adult patient'],
          'The distinction is examined often: obtaining consent is the provider\'s duty; witnessing and confirming understanding is the nurse\'s. If the patient cannot explain the procedure back, stop and call the provider.'),
        T('Therapeutic communication',
          'purposeful interaction that encourages the patient to express feelings and concerns',
          ['giving the patient clear advice about what they should do',
           'reassuring the patient that everything will be fine',
           'explaining the medical rationale for each intervention'],
          'Open-ended questions, reflection and silence are therapeutic. Advising, false reassurance and "why" questions are the classic non-therapeutic responses.'),
        T('Culturally competent care',
          'delivering care that respects a patient\'s cultural beliefs, values and language needs',
          ['treating every patient in exactly the same way regardless of background',
           'assigning patients to nurses of the same cultural background',
           'learning the customs of the largest local ethnic group'],
          'Identical treatment is not equitable treatment. Assessing the individual, rather than assuming from group membership, is the point — stereotyping in the name of cultural competence is itself an error.')
      ]
    },

    biology: {
      label: 'Biology',
      sections: [
        { id: 'concepts', name: 'Cell & Molecular Biology', weight: 30 },
        { id: 'applied', name: 'Genetics & Physiology', weight: 40 },
        { id: 'analysis', name: 'Evolution & Ecology', weight: 30 }
      ],
      refs: [REF.osBio],
      pool: [
        T('The role of DNA polymerase',
          'synthesising a new DNA strand by adding nucleotides complementary to a template',
          ['unwinding the double helix ahead of the replication fork',
           'joining Okazaki fragments on the lagging strand',
           'transcribing DNA into messenger RNA'],
          'Helicase unwinds, ligase joins fragments, RNA polymerase transcribes. Polymerase also proofreads, which is why replication error rates are so low.'),
        T('Meiosis compared with mitosis',
          'meiosis produces four genetically distinct haploid cells; mitosis produces two identical diploid cells',
          ['meiosis produces two haploid cells and mitosis four diploid cells',
           'both produce identical daughter cells but meiosis takes longer',
           'meiosis occurs in all body cells and mitosis only in gametes'],
          'Crossing over in prophase I and independent assortment are what generate the genetic variation. Mitosis is for growth and repair; meiosis is for gametes.'),
        T('An enzyme',
          'a biological catalyst that lowers activation energy without being consumed',
          ['a molecule that provides energy for cellular reactions',
           'a structural protein that gives cells their shape',
           'a signalling molecule that travels through the bloodstream'],
          'Enzymes are substrate-specific and sensitive to pH and temperature. Denaturation destroys the active site\'s shape, which is why it abolishes function.'),
        T('Natural selection',
          'differential survival and reproduction of individuals with heritable traits suited to the environment',
          ['the deliberate breeding of organisms for desired characteristics',
           'the gradual change of an individual organism over its lifetime',
           'random changes in allele frequency in small populations'],
          'The last option is genetic drift. Individuals do not evolve — populations do. Traits must be heritable for selection to act on them.'),
        T('Homeostasis',
          'maintenance of a stable internal environment despite external change',
          ['the tendency of organisms to grow toward an optimal size',
           'the balance of species numbers within an ecosystem',
           'the equal distribution of solutes across a membrane'],
          'Negative feedback is the mechanism — thermoregulation, blood glucose control, osmoregulation. Positive feedback is rare and amplifying, as in childbirth and blood clotting.'),
        T('A recessive allele',
          'one whose phenotype appears only when two copies are present',
          ['one that appears in the phenotype whenever it is present',
           'one located on the Y chromosome only',
           'one that arises from a recent mutation'],
          'Heterozygous carriers show the dominant phenotype. This is why recessive conditions can skip generations, and why Punnett squares are the standard tool.')
      ]
    },

    chemistry: {
      label: 'Chemistry',
      sections: [
        { id: 'concepts', name: 'Atomic Structure & Bonding', weight: 30 },
        { id: 'applied', name: 'Reactions & Stoichiometry', weight: 40 },
        { id: 'analysis', name: 'Equilibrium & Thermodynamics', weight: 30 }
      ],
      refs: [REF.osChem],
      pool: [
        T('An ionic bond',
          'the electrostatic attraction between oppositely charged ions after electron transfer',
          ['the sharing of one or more pairs of electrons between two atoms',
           'the attraction between the nuclei of adjacent atoms',
           'the weak attraction between temporary dipoles'],
          'Covalent bonds share; ionic bonds transfer. Large electronegativity differences — typically metal plus non-metal — produce ionic character.'),
        T('A mole',
          'the amount of substance containing 6.022 × 10²³ elementary entities',
          ['the mass in grams of one atom of an element',
           'the volume occupied by one gram of a gas at STP',
           'the number of protons in an atom\'s nucleus'],
          'Avogadro\'s number. It is the bridge between the mass you can weigh and the particle counts a balanced equation describes.'),
        T('An oxidising agent',
          'a species that gains electrons and is itself reduced',
          ['a species that loses electrons and is itself oxidised',
           'any substance containing oxygen',
           'a species that donates protons in solution'],
          'OIL RIG — Oxidation Is Loss, Reduction Is Gain of electrons. The agent does the opposite of what its name suggests, which is why this is so frequently missed.'),
        T('Le Chatelier\'s principle',
          'a system at equilibrium shifts to counteract an imposed change',
          ['reaction rate always doubles for each 10 °C rise in temperature',
           'the equilibrium constant changes when concentration changes',
           'catalysts shift equilibrium toward the products'],
          'Catalysts speed both directions equally and do not shift the position of equilibrium. Only temperature changes the equilibrium constant itself.'),
        T('A buffer solution',
          'a mixture of a weak acid and its conjugate base that resists change in pH',
          ['a solution with a pH of exactly 7',
           'a solution that neutralises any acid added to it completely',
           'a concentrated solution of a strong acid'],
          'Capacity is greatest when pH equals pKa, and the useful range is roughly pKa ± 1. The bicarbonate system is the body\'s principal buffer.')
      ]
    },

    statistics: {
      label: 'Statistics',
      sections: [
        { id: 'concepts', name: 'Descriptive Statistics', weight: 30 },
        { id: 'applied', name: 'Probability & Distributions', weight: 40 },
        { id: 'analysis', name: 'Inference & Interpretation', weight: 30 }
      ],
      refs: [REF.osStat],
      pool: [
        T('The median',
          'the middle value when data are ordered, which resists distortion by outliers',
          ['the arithmetic average of all values',
           'the most frequently occurring value',
           'the difference between the largest and smallest values'],
          'This is why median income is reported rather than mean — a few very high earners pull the mean upward but leave the median where most people actually are.'),
        T('A p-value',
          'the probability of observing data at least as extreme as these if the null hypothesis were true',
          ['the probability that the null hypothesis is true',
           'the probability that the results occurred by chance',
           'the size of the effect that was observed'],
          'The most misstated concept in the subject. It says nothing about the probability of any hypothesis, and nothing about effect size — a tiny effect can be highly significant in a large sample.'),
        T('A Type I error',
          'rejecting a true null hypothesis — a false positive',
          ['failing to reject a false null hypothesis',
           'using the wrong statistical test for the data',
           'drawing a sample that is not representative'],
          'Type II is the false negative. Alpha sets the Type I rate, conventionally 0.05. Lowering alpha reduces Type I risk but raises Type II risk.'),
        T('Standard deviation',
          'a measure of how far values typically lie from the mean',
          ['the difference between the highest and lowest values',
           'the average of all values in the dataset',
           'the proportion of values above the mean'],
          'In a normal distribution roughly 68% of values lie within one standard deviation, 95% within two — the basis of most confidence interval reasoning.'),
        T('A 95% confidence interval',
          'a range which, over repeated sampling, would contain the true parameter 95% of the time',
          ['a range containing 95% of the observations in the sample',
           'a range in which the sample mean falls 95% of the time',
           'a range showing there is a 95% chance the effect is real'],
          'The confidence is in the procedure, not in any single interval. Wider intervals mean less precision — usually from a smaller sample or greater variability.'),
        T('Random assignment',
          'allocating participants to conditions by chance, which supports causal inference',
          ['selecting participants from the population by chance',
           'presenting conditions in a randomly varied order',
           'choosing which variables to measure without preference'],
          'Random <em>sampling</em> supports generalisation to a population; random <em>assignment</em> supports causal claims. They are different tools solving different problems.')
      ]
    },

    english: {
      label: 'English & Communication',
      sections: [
        { id: 'concepts', name: 'Grammar & Mechanics', weight: 30 },
        { id: 'applied', name: 'Composition & Rhetoric', weight: 40 },
        { id: 'analysis', name: 'Analysis & Research', weight: 30 }
      ],
      refs: [REF.owl],
      pool: [
        T('A thesis statement',
          'a single sentence presenting the essay\'s central arguable claim',
          ['a sentence summarising what the essay will cover',
           'the opening sentence designed to attract the reader\'s attention',
           'the restatement of the main idea in the conclusion'],
          'It must be arguable. "This essay discusses social media" announces a topic; "Social media platforms should be legally liable for algorithmic amplification of misinformation" makes a claim someone could dispute.'),
        T('A comma splice',
          'joining two independent clauses with only a comma',
          ['omitting the comma before a coordinating conjunction',
           'placing a comma between a subject and its verb',
           'using too many commas in a single sentence'],
          'Three fixes: add a coordinating conjunction, upgrade to a semicolon, or split into two sentences.'),
        T('Ethos in rhetorical appeal',
          'persuasion through the credibility and character of the speaker',
          ['persuasion through emotional response in the audience',
           'persuasion through logical reasoning and evidence',
           'persuasion through the timeliness of the moment'],
          'Pathos is emotion, logos is logic, kairos is timing. Academic writing relies mainly on logos, supported by ethos through credible sourcing.'),
        T('A primary source',
          'an original, first-hand record such as a document, dataset or interview',
          ['a peer-reviewed article analysing several studies',
           'a textbook summarising the state of a field',
           'an encyclopaedia entry on the topic'],
          'A research article reporting the authors\' own experiment is primary; a review article discussing other people\'s experiments is secondary.'),
        T('Plagiarism',
          'presenting another person\'s words, ideas or data as your own without attribution',
          ['quoting a source at excessive length even with a citation',
           'using a source that has not been peer reviewed',
           'citing a source you have not personally read'],
          'It covers paraphrase and ideas, not only verbatim text — which is why changing a few words while keeping the structure still counts.'),
        T('An annotated bibliography',
          'a list of sources, each followed by a brief summary and evaluation',
          ['a list of every source consulted, whether cited or not',
           'a summary of an article placed before its main text',
           'the works-cited page of a completed research paper'],
          'The annotation typically covers what the source argues, how credible it is, and how it fits your project.')
      ]
    },

    history: {
      label: 'History, Humanities & Government',
      sections: [
        { id: 'concepts', name: 'Key Events & Ideas', weight: 30 },
        { id: 'applied', name: 'Institutions & Movements', weight: 40 },
        { id: 'analysis', name: 'Historical Analysis', weight: 30 }
      ],
      refs: [REF.osUS],
      pool: [
        T('Federalism in the United States',
          'the division of powers between a national government and state governments',
          ['the separation of powers among three branches of government',
           'the system by which courts may strike down legislation',
           'the process for amending the founding document'],
          'Separation of powers divides authority <em>horizontally</em> between branches; federalism divides it <em>vertically</em> between levels. Both are checks, but they are not the same mechanism.'),
        T('Judicial review',
          'the power of courts to declare legislative or executive acts unconstitutional',
          ['the review of a trial verdict by a higher court',
           'the process by which judges are confirmed to the bench',
           'the authority of courts to interpret their own procedures'],
          'Established in Marbury v. Madison (1803). It is not written explicitly into the Constitution — the Court asserted it.'),
        T('The Industrial Revolution\'s central change',
          'a shift from agrarian handcraft economies to machine-based manufacturing and factory production',
          ['the redistribution of farmland from nobles to peasants',
           'the replacement of monarchies by elected parliaments',
           'the expansion of global trade in agricultural commodities'],
          'It drove urbanisation, wage labour, and eventually organised labour movements — the social consequences are usually what essay questions target.'),
        T('A primary historical source',
          'a record created during the period under study by a participant or witness',
          ['a modern historian\'s interpretation of a past period',
           'a textbook chapter covering the period',
           'a documentary film about the events'],
          'Letters, treaties, photographs, census data, newspapers of the day. Historians assess them for authorship, audience, purpose and bias.'),
        T('The Enlightenment',
          'an 18th-century intellectual movement emphasising reason, individual rights and scepticism of traditional authority',
          ['a religious revival emphasising personal conversion',
           'a artistic movement prioritising emotion over reason',
           'an economic doctrine favouring state control of trade'],
          'Romanticism is the emotion-over-reason one, and it followed partly as a reaction. Enlightenment thought underpins constitutional government and natural rights arguments.')
      ]
    },

    criminal: {
      label: 'Criminal Justice & Law',
      sections: [
        { id: 'concepts', name: 'Legal Foundations', weight: 30 },
        { id: 'applied', name: 'Process & Procedure', weight: 40 },
        { id: 'analysis', name: 'Policy & Analysis', weight: 30 }
      ],
      refs: [REF.bjs],
      pool: [
        T('Probable cause',
          'reasonable grounds, based on facts, to believe a crime has been committed',
          ['a certainty beyond reasonable doubt that a suspect is guilty',
           'a vague suspicion based on an officer\'s experience',
           'the standard required to obtain a conviction at trial'],
          'The ladder of standards: reasonable suspicion (stop) < probable cause (arrest, warrant) < preponderance of evidence (civil) < clear and convincing < beyond reasonable doubt (criminal conviction).'),
        T('Miranda warnings',
          'advising a suspect of the rights to silence and counsel before custodial interrogation',
          ['informing a suspect of the charges at the time of arrest',
           'notifying a defendant of their trial date',
           'advising a witness of the penalties for perjury'],
          'Both elements must be present — custody <em>and</em> interrogation. Voluntary statements made before questioning are generally admissible without a warning.'),
        T('The exclusionary rule',
          'evidence obtained in violation of constitutional rights is generally inadmissible at trial',
          ['witnesses may be excluded from the courtroom during testimony',
           'jurors with prior knowledge of a case must be dismissed',
           'a defendant may exclude prior convictions from the record'],
          '"Fruit of the poisonous tree" extends it to derivative evidence. Good faith and inevitable discovery are the main exceptions.'),
        T('Mens rea',
          'the mental state or criminal intent required for an offence',
          ['the physical act that constitutes the offence',
           'the harm suffered by the victim',
           'the defendant\'s prior criminal record'],
          'Actus reus is the physical act. Most crimes require both. Strict liability offences are the exception, requiring no mens rea at all.'),
        T('A felony',
          'a serious offence generally punishable by more than one year of imprisonment',
          ['any offence involving violence against a person',
           'an offence tried without a jury',
           'an offence committed by someone with prior convictions'],
          'Misdemeanours carry up to a year, usually in local jail rather than prison. Felony conviction also triggers collateral consequences — voting, firearms, employment.'),
        T('Recidivism',
          'the tendency of a convicted person to reoffend after release',
          ['the rate at which crimes are reported to police',
           'the proportion of arrests that result in conviction',
           'the time between arrest and trial'],
          'It is the standard outcome measure for whether a correctional programme works, which is why rehabilitation and deterrence debates turn on it.')
      ]
    },

    education: {
      label: 'Education & Teaching',
      sections: [
        { id: 'concepts', name: 'Learning Theory', weight: 30 },
        { id: 'applied', name: 'Instruction & Assessment', weight: 40 },
        { id: 'analysis', name: 'Policy & Classroom Analysis', weight: 30 }
      ],
      refs: [REF.ed],
      pool: [
        T('Formative assessment',
          'assessment during learning used to give feedback and adjust teaching',
          ['assessment at the end of a unit used to assign a grade',
           'assessment comparing a student against a national sample',
           'assessment used to determine placement in a programme'],
          'Summative assessment is the end-of-unit grading one. Exit tickets, questioning and drafts are formative; a final exam is summative. The same instrument can serve either purpose depending on use.'),
        T('Scaffolding',
          'temporary support that is gradually withdrawn as a learner becomes competent',
          ['dividing a class into fixed ability groups',
           'providing the same instruction to every student simultaneously',
           'assessing prior knowledge before beginning a unit'],
          'It follows from Vygotsky\'s zone of proximal development — the gap between what a learner can do alone and with help. The withdrawal is essential; permanent support is not scaffolding.'),
        T('An IEP',
          'an Individualized Education Program — a legally binding plan for a student with a disability',
          ['a plan for students identified as academically gifted',
           'a school-wide improvement plan filed with the state',
           'a behaviour contract agreed between teacher and student'],
          'Required under IDEA. A 504 plan is the related but distinct accommodation route under civil rights law, with a broader eligibility definition and fewer procedural protections.'),
        T('Differentiated instruction',
          'adapting content, process or product to meet varied student readiness, interest and learning profile',
          ['teaching each student individually rather than as a class',
           'lowering expectations for students who are struggling',
           'grouping students permanently by measured ability'],
          'Differentiation adjusts the route, not the destination — the learning goal stays the same. Lowering the goal is modification, a different thing.'),
        T('Bloom\'s taxonomy',
          'a hierarchy of cognitive objectives from remembering through to creating',
          ['a classification of learning styles by sensory preference',
           'a sequence of stages in moral development',
           'a framework for classroom behaviour management'],
          'The revised order: Remember, Understand, Apply, Analyse, Evaluate, Create. It is used to write objectives at the intended level — "list" and "evaluate" demand very different work.')
      ]
    },

    ethics: {
      label: 'Ethics, Philosophy & Religion',
      sections: [
        { id: 'concepts', name: 'Ethical Theories', weight: 30 },
        { id: 'applied', name: 'Applied Ethics', weight: 40 },
        { id: 'analysis', name: 'Argument Analysis', weight: 30 }
      ],
      refs: [REF.owl],
      pool: [
        T('Utilitarianism',
          'judging actions right insofar as they produce the greatest overall wellbeing',
          ['judging actions by whether they follow universal moral duties',
           'judging actions by whether they express virtuous character',
           'judging actions by whether they respect an agreed social contract'],
          'Consequentialist. Deontology is the duty-based one (Kant), virtue ethics the character-based one (Aristotle). The standard objection is that utilitarianism can justify harming a minority for aggregate benefit.'),
        T('Kant\'s categorical imperative',
          'act only on a maxim you could will to become a universal law',
          ['act so as to maximise happiness for the greatest number',
           'act as a person of good character would act',
           'act according to the rules your society has agreed'],
          'The second formulation is often the tested one: treat humanity always as an end, never merely as a means. Duty and intention matter, not outcomes.'),
        T('A valid argument',
          'one whose conclusion must follow if its premises are true',
          ['one whose premises are all actually true',
           'one that most people find persuasive',
           'one supported by substantial empirical evidence'],
          'Validity concerns structure only. An argument can be valid with false premises. <strong>Sound</strong> means valid <em>and</em> with true premises — that is the pair examiners test.'),
        T('An ad hominem fallacy',
          'attacking the person making an argument rather than the argument itself',
          ['misrepresenting an argument to make it easier to refute',
           'assuming a conclusion within the premises',
           'appealing to popularity as evidence of truth'],
          'The second is a straw man, the third begging the question, the fourth an appeal to popularity. These four appear together as options constantly.'),
        T('Moral relativism',
          'the view that moral judgements are true or false only relative to a culture or individual',
          ['the view that some moral truths hold universally',
           'the view that moral statements express no belief at all',
           'the view that morality derives from divine command'],
          'The standard difficulty: it appears to make cross-cultural criticism of any practice incoherent, including practices most people want to condemn.')
      ]
    },

    general: {
      label: 'Academic Skills & Research Methods',
      sections: [
        { id: 'concepts', name: 'Core Concepts', weight: 30 },
        { id: 'applied', name: 'Application', weight: 40 },
        { id: 'analysis', name: 'Analysis & Research', weight: 30 }
      ],
      refs: [REF.owl],
      pool: [
        T('A peer-reviewed source',
          'one evaluated by independent experts in the field before publication',
          ['one published by a well-known commercial publisher',
           'one that has been cited by many other authors',
           'one written by an author with an advanced degree'],
          'Peer review is a process, not a credential. A blog post by a professor is not peer reviewed; an article in a scholarly journal is.'),
        T('Paraphrasing correctly',
          'restating a source\'s idea entirely in your own words and sentence structure, with a citation',
          ['changing several words in the original while keeping its structure',
           'summarising the source without a citation because it is in your words',
           'quoting the source but omitting the quotation marks'],
          'Keeping the original structure while swapping synonyms is still plagiarism — patchwriting. A citation is required whether you quote or paraphrase.'),
        T('A literature review',
          'a synthesis of existing scholarship that identifies themes, debates and gaps',
          ['a list of every source consulted with a summary of each',
           'a critical review of a single influential book',
           'a chronological account of a field\'s history'],
          'The word doing the work is <em>synthesis</em> — organising by theme or argument, not walking through one source after another.'),
        T('A dependent variable',
          'the outcome that is measured to see whether it changed',
          ['the factor the researcher deliberately manipulates',
           'a factor held constant across conditions',
           'an uncontrolled factor that distorts results'],
          'It "depends" on the independent variable. If you can point to what the researcher changed, that is the independent one.'),
        T('Reliability of a measure',
          'the consistency with which it produces the same result under the same conditions',
          ['the extent to which it measures what it claims to measure',
           'the degree to which findings generalise to other populations',
           'the ease with which it can be administered'],
          'Validity is the measures-what-it-claims one. A measure can be perfectly reliable and completely invalid — consistently wrong.')
      ]
    }
  };

  /* Aliases */
  SUBJECTS.physics = SUBJECTS.physics || {
    label: 'Physics',
    sections: SUBJECTS.chemistry.sections,
    refs: [{ t: 'OpenStax — College Physics 2e', u: 'https://openstax.org/details/books/college-physics-2e', k: 'scholarly' }],
    pool: [
      T('Newton\'s second law',
        'the net force on an object equals its mass times its acceleration',
        ['every action has an equal and opposite reaction',
         'an object in motion stays in motion unless acted on by a force',
         'energy can be neither created nor destroyed'],
        'F = ma. The first option is the third law, the second the first law, the third conservation of energy.'),
      T('A scalar quantity',
        'one having magnitude only, with no direction',
        ['one having both magnitude and direction',
         'one that changes with the observer\'s frame of reference',
         'one measured relative to a fixed origin'],
        'Speed, mass, temperature and energy are scalars. Velocity, force, acceleration and momentum are vectors.'),
      T('Conservation of momentum',
        'total momentum of an isolated system remains constant before and after a collision',
        ['kinetic energy is always conserved in any collision',
         'the heavier object always retains more velocity',
         'momentum is transferred entirely to the lighter object'],
        'Momentum is conserved in all collisions; kinetic energy only in elastic ones. That difference is what defines an inelastic collision.')
    ]
  };
  SUBJECTS.math = SUBJECTS.math || null; /* handled by the numeric generator */

  /* =====================================================================
     3. GENERATION
     ===================================================================== */

  function mulberry32(a) {
    return function () {
      a |= 0; a = a + 0x6D2B79F5 | 0;
      var t = Math.imul(a ^ a >>> 15, 1 | a);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  function yt2(q) { return yt(q); }

  function buildItem(r, subj, entry, difficulty, sectionId) {
    var opts = [entry.d].concat(entry.wrong.slice(0, 3));
    for (var i = opts.length - 1; i > 0; i--) {
      var j = Math.floor(r() * (i + 1));
      var tmp = opts[i]; opts[i] = opts[j]; opts[j] = tmp;
    }
    var stem = /^[A-Z]/.test(entry.t) && !/\?$/.test(entry.t)
      ? entry.t + ' is best described as:'
      : entry.t;

    return {
      section: sectionId,
      difficulty: difficulty,
      type: 'mcq',
      q: stem,
      opts: opts,
      a: opts.indexOf(entry.d),
      why: '<p><strong>' + entry.t + '</strong> \u2014 ' + entry.d + '.</p>' +
        (entry.extra ? '<p>' + entry.extra + '</p>' : '') +
        '<p>The distractors here are all genuine concepts from this subject attached to the wrong definition. That is how course exams are usually written, so learn each term <em>against its neighbours</em> rather than on its own.</p>',
      refs: (subj.refs || []).concat([yt2(entry.t + ' explained ' + subj.label)])
    };
  }

  /* Subjects where numeric problems are genuinely part of the course. Every
     other subject must never receive arithmetic filler — that was the MIS561
     complaint, and topping up from the numeric generator would reintroduce it
     the moment the subject pool ran dry. */
  var QUANT_SUBJECTS = { math: 1, statistics: 1, accounting: 1, finance: 1, physics: 1, chemistry: 1, economics: 1 };

  /* A reverse item: give the definition, ask for the term. This is a real
     second question type, not a reworded first one, and it roughly doubles
     the usable bank without writing new content. */
  function buildReverse(r, subj, entry, pool, difficulty, sectionId) {
    var others = pool.filter(function (x) { return x.t !== entry.t; });
    for (var i = others.length - 1; i > 0; i--) {
      var j = Math.floor(r() * (i + 1));
      var tp = others[i]; others[i] = others[j]; others[j] = tp;
    }
    if (others.length < 3) return null;

    var opts = [entry.t, others[0].t, others[1].t, others[2].t];
    for (var k = opts.length - 1; k > 0; k--) {
      var m = Math.floor(r() * (k + 1));
      var t2 = opts[k]; opts[k] = opts[m]; opts[m] = t2;
    }
    var def = entry.d.charAt(0).toUpperCase() + entry.d.slice(1);

    return {
      section: sectionId,
      difficulty: difficulty,
      type: 'mcq',
      q: 'Which term is defined as follows? \u201c' + def + '.\u201d',
      opts: opts,
      a: opts.indexOf(entry.t),
      why: '<p>That definition describes <strong>' + entry.t + '</strong>.</p>' +
        (entry.extra ? '<p>' + entry.extra + '</p>' : '') +
        '<p>Working from definition back to term is worth practising separately. Recognising a term you are shown is easier than retrieving it from a description, and course exams test both directions.</p>',
      refs: (subj.refs || []).concat([yt2(entry.t + ' explained ' + subj.label)])
    };
  }

  /* Public: generate n subject-relevant items for a course track. */
  function generateForCourse(opts) {
    opts = opts || {};
    var subject = opts.subject || 'general';
    var subj = SUBJECTS[subject] || SUBJECTS.general;
    var count = Math.max(1, opts.count || 10);
    var r = mulberry32(((opts.seed || 1) * 2654435761) % 2147483647);
    var diffs = ['easy', 'moderate', 'hard', 'advanced'];
    var secs = (opts.sections && opts.sections.length)
      ? opts.sections
      : subj.sections.map(function (s) { return s.id; });

    /* Widen the pool with the academic-skills bank. Research methods, source
       evaluation and citation are examinable in essentially every course, so
       these are relevant filler rather than padding. */
    var pool = subj.pool.slice();
    var extra = (subject !== 'general') ? SUBJECTS.general.pool : [];
    var distractorPool = pool.concat(extra);

    function candidatesFrom(list) {
      var c = [];
      list.forEach(function (entry, i) {
        c.push({ e: entry, rev: false, i: i });
        c.push({ e: entry, rev: true, i: i });
      });
      for (var a = c.length - 1; a > 0; a--) {
        var b = Math.floor(r() * (a + 1));
        var tmp = c[a]; c[a] = c[b]; c[b] = tmp;
      }
      return c;
    }

    /* Subject items are exhausted before any academic-skills item appears.
       Shuffling the two together let research-methods questions take roughly
       a third of an accounting paper, which is not what a student expects
       from a course exam. */
    var candidates = candidatesFrom(pool).concat(candidatesFrom(extra));

    var out = [];
    var seen = {};
    var idx = 0;
    var guard = 0;

    while (out.length < count && guard++ < count * 8) {
      var c = candidates[idx % candidates.length];
      idx++;
      var d = (opts.difficulty && opts.difficulty !== 'combined') ? opts.difficulty : diffs[out.length % 4];
      var sec = secs[out.length % secs.length];
      var item = c.rev
        ? buildReverse(r, subj, c.e, distractorPool, d, sec)
        : buildItem(r, subj, c.e, d, sec);
      if (!item) continue;

      /* Once every distinct item has been used, allow the bank to cycle
         rather than returning a short paper — repetition across a long
         sitting is spaced practice, not a defect. */
      if (seen[item.q] && out.length < candidates.length) continue;

      seen[item.q] = true;
      item.id = (opts.prefix || 'course') + '-sub' + (opts.seed || 0) + '-' + out.length;
      item.exam = opts.examId || '';
      item.generated = true;
      item.subject = subject;
      out.push(item);
    }
    return out;
  }

  window.XP_SUBJECTS = {
    classify: classify,
    subjects: SUBJECTS,
    sectionsFor: function (subject) {
      var s = SUBJECTS[subject];
      return (s && s.sections) || SUBJECTS.general.sections;
    },
    labelFor: function (subject) {
      var s = SUBJECTS[subject];
      return (s && s.label) || 'General Studies';
    },
    generate: generateForCourse,
    isQuant: function (subject) { return !!QUANT_SUBJECTS[subject]; }
  };
})();
